import { useState } from 'react'
import { useIndexesQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import type { Index } from '@/types/compatibility'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  HashtagIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'

// Extended Index type with backward compatibility properties
interface ExtendedIndex extends Index {
  index_name?: string
  tableName?: string
}

interface IndexesViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
}

export function IndexesView({ connection, database, schema }: IndexesViewProps) {
  const [expandedIndexes, setExpandedIndexes] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'table' | 'type' | 'cardinality'>('table')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [filterType, setFilterType] = useState<'all' | 'primary' | 'unique' | 'regular'>('all')

  // Detect database type for conditional rendering
  const isDatabasePostgreSQL = connection.type === 'postgresql'
  const isDatabaseMySQL = connection.type === 'mysql'

  const { data: indexesResponse, isLoading, error } = useIndexesQuery(
    connection.id,
    database,
    schema,
    { enabled: true, limit: 1000, databaseType: connection.type === 'mysql' ? 'MySQL' : 'PostgreSQL' }
  )

  // Ensure we have a proper array for filtering
  const indexes: ExtendedIndex[] = Array.isArray(indexesResponse?.data)
    ? (indexesResponse.data as unknown[])
        .filter((i): i is unknown => i !== null)
        .map((i) => {
          const obj = (i ?? {}) as Record<string, unknown>
          const columnsUnknown = obj.columns
          let columns: { name?: string; ascending?: boolean }[] | undefined
          if (Array.isArray(columnsUnknown)) {
            columns = columnsUnknown.map((c: unknown) => {
              if (c && typeof c === 'object') {
                const name = typeof (c as { name?: unknown }).name === 'string' ? (c as { name?: string }).name : undefined
                const ascending = typeof (c as { ascending?: unknown }).ascending === 'boolean' ? (c as { ascending?: boolean }).ascending : undefined
                return { name, ascending }
              }
              const name = typeof c === 'string' ? c : (typeof c === 'number' ? String(c) : '')
              return { name }
            })
          }
          return {
            ...(obj as Index),
            index_name: typeof obj.index_name === 'string' ? obj.index_name : undefined,
            tableName: typeof obj.tableName === 'string' ? obj.tableName : undefined,
            columns
          } as ExtendedIndex
        })
    : []

  // Filter and sort indexes
  const filteredAndSortedIndexes = indexes
    .filter((index: ExtendedIndex) => {
      // Search filter
      const matchesSearch = !searchTerm || 
        index.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        index.index_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        index.table?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        index.tableName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        index.columns && index.columns.some((col) => {
          return col.name?.toLowerCase().includes(searchTerm.toLowerCase())
        })

      // Type filter
      const matchesType = filterType === 'all' || 
        (filterType === 'primary' && index.primary) ||
        (filterType === 'unique' && index.unique && !index.primary) ||
        (filterType === 'regular' && !index.primary && !index.unique)

      return matchesSearch && matchesType
    })
    .sort((a: ExtendedIndex, b: ExtendedIndex) => {
      let aValue: string | number = ''
      let bValue: string | number = ''

      switch (sortBy) {
        case 'name':
          aValue = a.name || a.index_name || ''
          bValue = b.name || b.index_name || ''
          break
        case 'table':
          aValue = a.table || a.tableName || ''
          bValue = b.table || b.tableName || ''
          break
        case 'type':
          aValue = a.type || ''
          bValue = b.type || ''
          break
        case 'cardinality':
          aValue = a.cardinality || 0
          bValue = b.cardinality || 0
          break
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue
      }

      const comparison = String(aValue).localeCompare(String(bValue))
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const toggleIndexExpansion = (indexKey: string) => {
    const newExpanded = new Set(expandedIndexes)
    if (newExpanded.has(indexKey)) {
      newExpanded.delete(indexKey)
    } else {
      newExpanded.add(indexKey)
    }
    setExpandedIndexes(newExpanded)
  }

  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const getIndexIcon = (index: ExtendedIndex) => {
    if (index.primary) {
      return <div className="w-4 h-4 flex items-center justify-center text-yellow-600 dark:text-yellow-400 font-bold text-xs">🔑</div>
    }
    if (index.unique) {
      return <div className="w-4 h-4 flex items-center justify-center text-green-600 dark:text-green-400 font-bold text-xs">U</div>
    }
    return <HashtagIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
  }

  const getSortIcon = (field: typeof sortBy) => {
    if (sortBy !== field) {
      return <ArrowsUpDownIcon className="w-4 h-4 text-gray-400" />
    }
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading indexes: {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <HashtagIcon className="w-6 h-6 mr-2 text-indigo-600" />
              Indexes
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedIndexes.length} indexes
            </p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search indexes, tables, or columns..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value) }}
            />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={filterType}
              onChange={(e) => {
                const v = e.target.value
                if (v === 'all' || v === 'primary' || v === 'unique' || v === 'regular') {
                  setFilterType(v)
                } else {
                  setFilterType('all')
                }
              }}
            >
              <option value="all">All Types</option>
              <option value="primary">Primary Keys</option>
              <option value="unique">Unique Indexes</option>
              <option value="regular">Regular Indexes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Indexes Table */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          {/* Table Header */}
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className="flex items-center px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div className="w-8 flex-shrink-0"></div> {/* Expand icon */}
              <div 
                className="min-w-[200px] flex-1 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => { handleSort('name') }}
              >
                Index Name
                {getSortIcon('name')}
              </div>
              <div 
                className="min-w-[150px] flex-1 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => { handleSort('table') }}
              >
                Table
                {getSortIcon('table')}
              </div>
              <div 
                className="min-w-[100px] flex-shrink-0 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => { handleSort('type') }}
              >
                Type
                {getSortIcon('type')}
              </div>
              <div className="min-w-[120px] flex-shrink-0">Properties</div>
              {/* Only show cardinality if it's commonly available for this database type */}
              {isDatabaseMySQL && (
                <div 
                  className="min-w-[100px] flex-shrink-0 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                  onClick={() => { handleSort('cardinality') }}
                >
                  Cardinality
                  {getSortIcon('cardinality')}
                </div>
              )}
              {/* Show schema column only for PostgreSQL */}
              {isDatabasePostgreSQL && (
                <div className="min-w-[100px] flex-shrink-0">Schema</div>
              )}
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedIndexes.length === 0 ? (
              <div className="text-center py-12">
                <HashtagIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm || filterType !== 'all' ? 'No indexes match your criteria' : 'No indexes found'}
                </p>
              </div>
            ) : (
              filteredAndSortedIndexes.map((index: ExtendedIndex, indexIdx: number) => {
                const indexKey = `${index.table || index.tableName || 'unknown'}-${index.name || index.index_name || indexIdx}`
                const isExpanded = expandedIndexes.has(indexKey)
                const hasColumns = index.columns && index.columns.length > 0

                return (
                  <div key={indexKey}>
                    {/* Index Row */}
                    <div className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      {/* Expand/Collapse */}
                      <div className="w-8 flex-shrink-0 flex items-center">
                        {hasColumns && (
                          <button type="button"
                            onClick={() => { toggleIndexExpansion(indexKey) }}
                            className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Index Name */}
                      <div className="min-w-[200px] flex-1 flex items-center">
                        {getIndexIcon(index)}
                        <span className="ml-2 font-medium text-gray-900 dark:text-white truncate">
                          {index.name || index.index_name || 'Unnamed Index'}
                        </span>
                      </div>

                      {/* Table Name */}
                      <div className="min-w-[150px] flex-1 flex items-center">
                        <TableCellsIcon className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 truncate">
                          {index.table || index.tableName || 'Unknown Table'}
                        </span>
                      </div>

                      {/* Type */}
                      <div className="min-w-[100px] flex-shrink-0">
                        {index.type ? (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs">
                            {index.type}
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs">
                            Unknown
                          </span>
                        )}
                      </div>

                      {/* Properties */}
                      <div className="min-w-[120px] flex-shrink-0">
                        <div className="flex flex-wrap gap-1">
                          {index.primary && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                              PRIMARY
                            </span>
                          )}
                          {index.unique && !index.primary && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                              UNIQUE
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Cardinality (MySQL specific) */}
                      {isDatabaseMySQL && (
                        <div className="min-w-[100px] flex-shrink-0 text-gray-700 dark:text-gray-300">
                          {typeof index.cardinality === 'number' && !Number.isNaN(index.cardinality) && index.cardinality >= 0
                            ? index.cardinality.toLocaleString() 
                            : <span className="text-gray-400 dark:text-gray-500">N/A</span>}
                        </div>
                      )}

                      {/* Schema (PostgreSQL specific) */}
                      {isDatabasePostgreSQL && (
                        <div className="min-w-[100px] flex-shrink-0 text-gray-700 dark:text-gray-300">
                          {index.schema || 'public'}
                        </div>
                      )}
                    </div>

                    {/* Expanded Columns */}
                    {isExpanded && hasColumns && (
                      <div className="bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-600">
                        <div className="px-12 py-4">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Index Columns ({index.columns!.length}):
                          </div>
                          <div className="grid gap-2">
                            {index.columns!.map((column, columnIndex) => {
                              try {
                                // Handle column format
                                const columnName = column.name || 'unknown'
                                // Handle ascending format (true/false)
                                const columnOrder: 'ASC' | 'DESC' = column.ascending === false ? 'DESC' : 'ASC'
                                
                                return (
                                  <div 
                                    key={columnIndex}
                                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
                                  >
                                    <div className="flex items-center">
                                      <span className="text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2 py-1 rounded mr-3">
                                        {columnIndex + 1}
                                      </span>
                                      <TableCellsIcon className="w-4 h-4 text-gray-400 mr-2" />
                                      <span className="font-mono text-sm text-gray-900 dark:text-white">
                                        {columnName}
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-1 rounded text-xs ${
                                        columnOrder === 'DESC' 
                                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300' 
                                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                                      }`}>
                                        {columnOrder === 'DESC' ? 'DESC ↓' : 'ASC ↑'}
                                      </span>
                                      {columnIndex === 0 && index.columns!.length > 1 && (
                                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 rounded text-xs">
                                          Primary Sort
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )
                              } catch (error) {
                                console.warn('Error rendering expanded column:', column, error)
                                return (
                                  <div 
                                    key={columnIndex}
                                    className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-600"
                                  >
                                    <div className="flex items-center">
                                      <span className="text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded mr-3">
                                        {columnIndex + 1}
                                      </span>
                                      <span className="text-sm text-red-600 dark:text-red-400">
                                        Invalid column data
                                      </span>
                                    </div>
                                  </div>
                                )
                              }
                            })}
                          </div>

                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedIndexes.length} of {indexes.length} indexes
          </div>
          <div className="flex items-center space-x-4">
            <span>Primary: {indexes.filter((i: ExtendedIndex) => i.primary).length}</span>
            <span>Unique: {indexes.filter((i: ExtendedIndex) => i.unique && !i.primary).length}</span>
            <span>Regular: {indexes.filter((i: ExtendedIndex) => !i.primary && !i.unique).length}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
